/**
 * Builder Service
 * Manages the Multi-Slide Builder Agent lifecycle and state
 * 
 * Architecture:
 * - Runs in TASKPANE context (persists while add-in is open)
 * - Agent continues running even when planning modal is closed
 * - Dialog UI accesses state via window bridge
 * - Subscribes to Python backend WebSocket for real-time progress
 * - Uses GlobalStateManager conversation registry for agent persistence
 */

import GlobalStateManager from "../../main/globals/global-state/globalStateManager";
import { authService } from "@carousel-office/shared";
import { initializeAuthIntegration } from "../../main/globals/authIntegration";
import { backendClient, ProgressUpdate } from "../../services/communication/backendClient";
import type { PresentationPlan } from "@carousel-office/shared/types/planning";
import type { 
  PersistedConversationStatus, 
  PersistedBuildProgress,
  PersistedSectionState,
} from "../../main/globals/persistence";

// Import the builder agents
import { MultiSlideBuilderAgent } from "../../main/agents/builder";
import type { MultiSlideBuilderParams } from "../../main/agents/builder";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

/** Build status states */
export type BuildStatus = 
  | 'idle'           // No build active
  | 'initializing'   // Calling /build/init
  | 'queued'         // Waiting in queue to start
  | 'building'       // Agent is running
  | 'paused'         // User requested pause, agent will stop after current area
  | 'completed'      // Build finished successfully
  | 'failed'         // Build failed with error
  | 'cancelled';     // User cancelled build

/** Error that occurred during build */
export interface BuildError {
  timestamp: string;
  message: string;
  traceback?: string;
  sectionId?: string;
  areaId?: string;
  retryCount: number;
}

/** Progress for current section */
export interface SectionProgress {
  sectionId: string;
  sectionIndex: number;
  sectionName: string;
  status: 'pending' | 'building' | 'completed' | 'failed' | 'skipped';
  slidesBuilt: number;
  totalSlides: number;
  currentAreaId?: string;
  startedAt?: string;
  completedAt?: string;
}

/** Overall build progress state */
export interface BuildProgress {
  planId: string;
  planTitle: string;
  status: BuildStatus;
  
  // Section-level progress
  currentSectionIndex: number;
  totalSections: number;
  sections: SectionProgress[];
  
  // Slide-level progress
  slidesBuilt: number;
  totalSlides: number;
  percentComplete: number;
  
  // Current activity
  currentArea?: string;
  currentPhase?: 'research' | 'excel' | 'building' | 'checkpointing';
  
  // Timing
  startedAt?: string;
  pausedAt?: string;
  completedAt?: string;
  
  // Errors
  errors: BuildError[];
  lastError?: BuildError;
  
  // Working files (from Python)
  workingPresentationPath?: string;
  workingWorkbookPath?: string;
  
  // Slide dimensions (from Python, in points)
  slideDimensions?: { width: number; height: number };
  
  // UI state
  /** Whether the user has viewed this completed build (for "New" badge) */
  seen?: boolean;
}

/** Build initialization response from Python */
export interface BuildInitResponse {
  plan_id: string;
  status: string;
  working_presentation_path: string;
  working_workbook_path: string;
  build_state: Record<string, unknown>;
  slide_dimensions: { width: number; height: number };
}

/** Entry for a build waiting in the queue */
export interface QueuedBuildEntry {
  planId: string;
  planTitle: string;
  position: number;
  status: 'queued';
  enqueuedAt: string;
  conversationId: string;
  documentId: string;
  styleGuidedocumentId: string;
  styleGuidePath?: string;
  slidesBase64?: string[];
  plan: PresentationPlan;
  totalSlides: number;
  totalSections: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// WEBSOCKET MESSAGE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Discriminated union for build-related WebSocket messages */
export type BuildWebSocketMessage =
  | {
      update_type: 'build_area_completed';
      update_data: {
        area_id: string;
        slide_index: number;
        slides_built?: number;
        section_slides_built?: number;
      };
    }
  | {
      update_type: 'build_section_started';
      update_data: {
        section_index: number;
        section_name: string;
      };
    }
  | {
      update_type: 'build_section_completed';
      update_data: {
        section_index: number;
        section_name: string;
      };
    }
  | {
      update_type: 'build_error';
      update_data: {
        error?: string;
        message?: string;
        traceback?: string;
        section_id?: string;
        area_id?: string;
        retry_count?: number;
        fatal?: boolean;
      };
    }
  | {
      update_type: 'build_completed';
      update_data?: Record<string, unknown>;
    }
  | {
      update_type: 'build_paused';
      update_data?: Record<string, unknown>;
    };

/** Type guard for build WebSocket messages */
function isBuildWebSocketMessage(update: ProgressUpdate): update is BuildWebSocketMessage & ProgressUpdate {
  const updateType = (update as any).update_type || update.type;
  return typeof updateType === 'string' && updateType.startsWith('build_');
}

/** 
 * Callbacks for progress updates.
 * 
 * Currently used internally by WebSocket handlers. The UI subscribes to aggregated updates
 * via window bridge (__subscribeToBuildUpdates), which calls onProgressUpdate.
 * 
 * Individual callbacks (onSectionStart, onSectionComplete, onAreaComplete) are available
 * for future use if the UI needs more granular event handling.
 */
export interface BuildProgressCallbacks {
  onProgressUpdate?: (progress: BuildProgress) => void;
  onSectionStart?: (sectionIndex: number, sectionName: string) => void;
  onSectionComplete?: (sectionIndex: number, sectionName: string) => void;
  onAreaComplete?: (areaId: string, slideIndex: number) => void;
  onError?: (error: BuildError) => void;
  onStatusChange?: (status: BuildStatus, previousStatus: BuildStatus) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// SERVICE STATE
// ═══════════════════════════════════════════════════════════════════════════

let isServiceInitialized = false;

/** Active build state - the build currently running */
let activeBuild: BuildProgress | null = null;

/** Queue of builds waiting to start */
let buildQueue: QueuedBuildEntry[] = [];

/** Recently completed builds - kept for UI display (cleared after 5 minutes) */
let completedBuilds: BuildProgress[] = [];

/** Pause/cancel request flags - checked by agent loop */
let pauseRequested = false;
let cancelRequested = false;

/** Active build's original parameters (for re-queueing when paused) */
let activeBuildParams: {
  conversationId: string;
  documentId: string;
  styleGuidedocumentId: string;
  styleGuidePath?: string;
  slidesBase64?: string[];
  plan: PresentationPlan;
} | null = null;

/** WebSocket progress handler reference for cleanup */
let wsProgressHandler: ((update: ProgressUpdate) => void) | null = null;

/** Registered callbacks for progress updates */
const progressCallbacks = new Set<BuildProgressCallbacks>();

/** Build conversation messages - keyed by planId */
const buildMessages = new Map<string, BuildConversationMessage[]>();

/** Counter for generating unique message IDs */
let messageIdCounter = 0;

// ═══════════════════════════════════════════════════════════════════════════
// MESSAGE TRACKING
// ═══════════════════════════════════════════════════════════════════════════

/** Message type for build conversation */
export interface BuildConversationMessage {
  id: string;
  timestamp: string;
  type: 'assistant' | 'tool_call' | 'tool_result' | 'system' | 'error';
  content?: string;
  toolCall?: {
    name: string;
    params?: Record<string, unknown>;
    status: 'running' | 'complete' | 'error';
    result?: unknown;
  };
  /** 
   * For nested tool calls/messages: which sub-agent this belongs to.
   * e.g., "presentationLibrarySearchAgent", "alphaSenseSearchAgent"
   * If undefined, this is a top-level message from MultiSlideBuilderAgent.
   */
  agentName?: string;
}

/**
 * Add a message to the build conversation
 */
function addBuildMessage(planId: string, message: Omit<BuildConversationMessage, 'id' | 'timestamp'>): BuildConversationMessage {
  const fullMessage: BuildConversationMessage = {
    ...message,
    id: `msg_${++messageIdCounter}_${Date.now()}`,
    timestamp: new Date().toISOString(),
  };
  
  const messages = buildMessages.get(planId) || [];
  messages.push(fullMessage);
  buildMessages.set(planId, messages);
  
  // Notify subscribers about the new message
  notifyProgressUpdate();
  
  return fullMessage;
}

/**
 * Update the last tool_call message with result
 */
function updateLastToolMessage(planId: string, status: 'complete' | 'error', result?: unknown): void {
  const messages = buildMessages.get(planId);
  if (!messages || messages.length === 0) return;
  
  // Find the last tool_call message
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].type === 'tool_call' && messages[i].toolCall?.status === 'running') {
      messages[i].toolCall!.status = status;
      messages[i].toolCall!.result = result;
      break;
    }
  }
}

/**
 * Get messages for a build
 */
export function getBuildMessages(planId: string): BuildConversationMessage[] {
  return buildMessages.get(planId) || [];
}

/**
 * Clear messages for a build
 */
function clearBuildMessages(planId: string): void {
  buildMessages.delete(planId);
}

// ═══════════════════════════════════════════════════════════════════════════
// SERVICE INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Initialize the builder service
 * Sets up GlobalStateManager and WebSocket connection
 */
export async function initializeBuilderService(): Promise<void> {
  if (isServiceInitialized) {
    console.log('[BuilderService] Already initialized');
    return;
  }

  try {
    console.log('[BuilderService] Initializing...');
    
    // Get JWT token from auth service
    await authService.reloadFromStorage();
    const jwtToken = authService.getAccessToken();
    
    if (!jwtToken) {
      throw new Error('JWT token required. Please ensure you are authenticated.');
    }
    
    // Initialize GlobalStateManager
    const globalState = GlobalStateManager.getInstance();
    await globalState.initialize(jwtToken);
    
    // Initialize auth integration
    await initializeAuthIntegration();
    
    // Set up WebSocket progress handler
    setupWebSocketHandler();
    
    isServiceInitialized = true;
    console.log('[BuilderService] Initialized successfully');
    
    // Load persisted builds from disk (paused/active builds from previous session)
    await loadPersistedBuilds();
    
  } catch (error) {
    console.error('[BuilderService] Initialization failed:', error);
    isServiceInitialized = false;
    throw error;
  }
}

/**
 * Set up WebSocket handler for build progress updates
 */
function setupWebSocketHandler(): void {
  if (wsProgressHandler) {
    backendClient.offProgress(wsProgressHandler);
  }
  
  wsProgressHandler = (update: ProgressUpdate) => {
    // Filter for build-related updates using type guard
    if (!isBuildWebSocketMessage(update)) {
      return; // Not a build update
    }
    
    const buildUpdate = update as BuildWebSocketMessage;
    console.log('[BuilderService] WebSocket build update:', buildUpdate.update_type, update);
    
    // Handle different build update types with proper typing
    switch (buildUpdate.update_type) {
      case 'build_area_completed':
        handleAreaCompleted(buildUpdate);
        break;
      case 'build_section_started':
        handleSectionStarted(buildUpdate);
        break;
      case 'build_section_completed':
        handleSectionCompleted(buildUpdate);
        break;
      case 'build_error':
        handleBuildError(buildUpdate);
        break;
      case 'build_completed':
        handleBuildCompleted(buildUpdate);
        break;
      case 'build_paused':
        handleBuildPaused(buildUpdate);
        break;
      default:
        // Runtime safety: log any unknown build_* message types not yet defined in BuildWebSocketMessage
        // The type guard isBuildWebSocketMessage() accepts any build_* prefix at runtime
        console.log('[BuilderService] Unknown build update type:', (buildUpdate as { update_type: string }).update_type);
    }
  };
  
  backendClient.onProgress(wsProgressHandler);
  console.log('[BuilderService] WebSocket handler registered');
}

// ═══════════════════════════════════════════════════════════════════════════
// WEBSOCKET EVENT HANDLERS
// ═══════════════════════════════════════════════════════════════════════════

function handleAreaCompleted(update: Extract<BuildWebSocketMessage, { update_type: 'build_area_completed' }>): void {
  if (!activeBuild) return;
  
  const { area_id: areaId, slide_index: slideIndex, slides_built, section_slides_built } = update.update_data;
  
  // Update progress
  activeBuild.slidesBuilt = slides_built ?? activeBuild.slidesBuilt;
  activeBuild.percentComplete = (activeBuild.slidesBuilt / activeBuild.totalSlides) * 100;
  activeBuild.currentArea = undefined;
  
  // Update current section
  if (activeBuild.sections[activeBuild.currentSectionIndex]) {
    activeBuild.sections[activeBuild.currentSectionIndex].slidesBuilt = 
      section_slides_built ?? activeBuild.sections[activeBuild.currentSectionIndex].slidesBuilt + 1;
  }
  
  // Notify callbacks
  notifyCallbacks('onAreaComplete', areaId, slideIndex);
  notifyProgressUpdate();
}

function handleSectionStarted(update: Extract<BuildWebSocketMessage, { update_type: 'build_section_started' }>): void {
  if (!activeBuild) return;
  
  const { section_index: sectionIndex, section_name: sectionName } = update.update_data;
  
  activeBuild.currentSectionIndex = sectionIndex;
  activeBuild.currentPhase = 'research';
  
  if (activeBuild.sections[sectionIndex]) {
    activeBuild.sections[sectionIndex].status = 'building';
    activeBuild.sections[sectionIndex].startedAt = new Date().toISOString();
  }
  
  notifyCallbacks('onSectionStart', sectionIndex, sectionName);
  notifyProgressUpdate();
}

async function handleSectionCompleted(update: Extract<BuildWebSocketMessage, { update_type: 'build_section_completed' }>): Promise<void> {
  if (!activeBuild) return;
  
  const { section_index: sectionIndex, section_name: sectionName } = update.update_data;
  
  if (activeBuild.sections[sectionIndex]) {
    activeBuild.sections[sectionIndex].status = 'completed';
    activeBuild.sections[sectionIndex].completedAt = new Date().toISOString();
  }
  
  notifyCallbacks('onSectionComplete', sectionIndex, sectionName);
  notifyProgressUpdate();
  
  // Persist progress after section completion (checkpoint)
  await persistBuildState('active');
}

function handleBuildError(update: Extract<BuildWebSocketMessage, { update_type: 'build_error' }>): void {
  if (!activeBuild) return;
  
  const data = update.update_data;
  const error: BuildError = {
    timestamp: new Date().toISOString(),
    message: data.error || data.message || 'Unknown error',
    traceback: data.traceback,
    sectionId: data.section_id,
    areaId: data.area_id,
    retryCount: data.retry_count ?? 0,
  };
  
  activeBuild.errors.push(error);
  activeBuild.lastError = error;
  
  // If fatal error, mark as failed
  if (data.fatal) {
    const previousStatus = activeBuild.status;
    activeBuild.status = 'failed';
    notifyCallbacks('onStatusChange', 'failed', previousStatus);
  }
  
  notifyCallbacks('onError', error);
  notifyProgressUpdate();
}

async function handleBuildCompleted(_update: Extract<BuildWebSocketMessage, { update_type: 'build_completed' }>): Promise<void> {
  if (!activeBuild) return;
  
  const previousStatus = activeBuild.status;
  activeBuild.status = 'completed';
  activeBuild.completedAt = new Date().toISOString();
  activeBuild.percentComplete = 100;
  
  notifyCallbacks('onStatusChange', 'completed', previousStatus);
  notifyProgressUpdate();
  
  // Persist completed state
  await persistBuildState('completed');
  
  // Move to completed list and process next in queue
  moveActiveToCompleted();
  processNextInQueue().catch(err => {
    console.error('[BuilderService] Error processing next in queue:', err);
  });
}

async function handleBuildPaused(_update: Extract<BuildWebSocketMessage, { update_type: 'build_paused' }>): Promise<void> {
  if (!activeBuild) return;
  
  const previousStatus = activeBuild.status;
  activeBuild.status = 'paused';
  activeBuild.pausedAt = new Date().toISOString();
  
  notifyCallbacks('onStatusChange', 'paused', previousStatus);
  notifyProgressUpdate();
  
  // Persist paused state
  await persistBuildState('paused');
}

// ═══════════════════════════════════════════════════════════════════════════
// BUILD LIFECYCLE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Start a new build for a plan
 * 
 * If a build is already in progress, this will add the new build to the queue
 * and return a BuildProgress with status 'queued'.
 * 
 * @param planId - The plan ID to build
 * @param plan - The plan object with sections and slides
 * @param styleGuidedocumentId - ID of the style guide deck
 * @param styleGuidePath - Optional path to style guide file
 * @param slidesBase64 - Optional array of slide base64 data for live style guides
 */
export async function startBuild(
  planId: string,
  plan: PresentationPlan,
  styleGuidedocumentId: string,
  styleGuidePath?: string,
  slidesBase64?: string[]
): Promise<BuildProgress> {
  console.log('[BuilderService] startBuild() called with:', {
    planId,
    planId_fromPlan: plan?.id,
    sectionCount: plan?.sections?.length,
    slideCount: plan?.slideCount,
    styleGuidedocumentId,
    styleGuidePath,
    slidesBase64Count: slidesBase64?.length || 0,
    isServiceInitialized,
    activeBuildStatus: activeBuild?.status,
    queueLength: buildQueue.length,
  });
  
  if (!isServiceInitialized) {
    console.error('[BuilderService] Service not initialized!');
    throw new Error('Builder service not initialized. Call initializeBuilderService() first.');
  }
  
  // Calculate totals for queue entry
  const totalSections = plan.sections.length;
  const totalSlides = plan.sections.reduce((sum, s) => sum + s.slides.length, 0);
  const planTitle = plan.sections[0]?.name || 'Untitled Presentation';
  
  // If a build is already in progress, add to queue instead
  if (activeBuild && activeBuild.status === 'building') {
    console.log(`[BuilderService] Active build in progress, adding ${planId} to queue`);
    
    const queueEntry: QueuedBuildEntry = {
      planId,
      planTitle,
      position: buildQueue.length,
      status: 'queued',
      enqueuedAt: new Date().toISOString(),
      conversationId: `builder_${planId}`,
      documentId: styleGuidedocumentId,
      styleGuidedocumentId,
      styleGuidePath,
      slidesBase64,
      plan,
      totalSlides,
      totalSections,
    };
    
    await enqueueBuild(queueEntry);
    
    // Return a queued BuildProgress for UI display
    return createQueuedBuildProgress(queueEntry);
  }
  
  // No active build, start immediately
  return startBuildInternal(planId, plan, styleGuidedocumentId, styleGuidePath, slidesBase64);
}

/**
 * Internal function to start a build (bypasses queue check)
 * Called directly by startBuildFromQueueEntry() for queued builds.
 * 
 * @param planId - The plan ID to build
 * @param plan - The plan object with sections and slides
 * @param styleGuidedocumentId - ID of the style guide deck
 * @param styleGuidePath - Optional path to style guide file
 * @param slidesBase64 - Optional array of slide base64 data for live style guides
 */
async function startBuildInternal(
  planId: string,
  plan: PresentationPlan,
  styleGuidedocumentId: string,
  styleGuidePath?: string,
  slidesBase64?: string[]
): Promise<BuildProgress> {
  console.log(`[BuilderService] Starting build for plan: ${planId}`);
  
  // Reset flags
  pauseRequested = false;
  cancelRequested = false;
  
  // Calculate totals
  const totalSections = plan.sections.length;
  const totalSlides = plan.sections.reduce((sum, s) => sum + s.slides.length, 0);
  
  // Initialize progress state
  // Use the first section name as the plan title, or a default
  const planTitle = plan.sections[0]?.name || 'Untitled Presentation';
  
  activeBuild = {
    planId,
    planTitle,
    status: 'initializing',
    currentSectionIndex: 0,
    totalSections,
    sections: plan.sections.map((section, index) => ({
      sectionId: section.id,
      sectionIndex: index,
      sectionName: section.name,
      status: 'pending',
      slidesBuilt: 0,
      totalSlides: section.slides.length,
    })),
    slidesBuilt: 0,
    totalSlides,
    percentComplete: 0,
    errors: [],
    startedAt: new Date().toISOString(),
  };
  
  // Store params for potential re-queueing if this build gets paused
  activeBuildParams = {
    conversationId: `builder_${planId}`,
    documentId: styleGuidedocumentId,
    styleGuidedocumentId,
    styleGuidePath,
    slidesBase64,
    plan,
  };
  
  // Notify UI immediately so job shows up in "In Progress" right away
  // Don't wait for backend init to complete
  notifyProgressUpdate();
  
  try {
    // Call Python /build/init to create working files
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${backendClient.baseUrl}/plans/${planId}/build/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        style_guide_deck_id: styleGuidedocumentId,
        style_guide_path: styleGuidePath,
        slides_base64: slidesBase64,
        total_sections: totalSections,
        total_slides: totalSlides,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to initialize build: ${errorText}`);
    }
    
    const initResult: BuildInitResponse = await response.json();
    console.log('[BuilderService] Build initialized:', initResult);
    
    // Update state with working file paths and dimensions
    activeBuild.workingPresentationPath = initResult.working_presentation_path;
    activeBuild.workingWorkbookPath = initResult.working_workbook_path;
    activeBuild.slideDimensions = initResult.slide_dimensions;
    activeBuild.status = 'building';
    
    console.log(`[BuilderService] Slide dimensions: ${initResult.slide_dimensions.width} x ${initResult.slide_dimensions.height} points`);
    
    // Connect WebSocket for this plan
    backendClient.connectProgressWebSocket(planId);
    
    // Register build conversation in GSM
    const globalState = GlobalStateManager.getInstance();
    const conversationId = `builder_${planId}`;
    globalState.createConversation(conversationId, styleGuidedocumentId, 'other');
    console.log(`[BuilderService] Registered conversation: ${conversationId}`);
    
    // Store active build params for potential re-queueing
    activeBuildParams = {
      conversationId,
      documentId: styleGuidedocumentId,
      styleGuidedocumentId,
      styleGuidePath,
      slidesBase64,
      plan,
    };
    
    // Notify status change
    notifyCallbacks('onStatusChange', 'building', 'initializing');
    notifyProgressUpdate();
    
    // Persist the initial build state
    await persistBuildState('active');
    
    // Start the agent loop (async - does not block)
    runBuildAgent(
      planId,
      plan,
      styleGuidedocumentId,
      initResult.working_presentation_path,
      initResult.working_workbook_path,
      initResult.slide_dimensions
    ).catch(async (error) => {
      console.error('[BuilderService] Agent loop error:', error);
      if (activeBuild) {
        activeBuild.status = 'failed';
        activeBuild.lastError = {
          timestamp: new Date().toISOString(),
          message: error instanceof Error ? error.message : 'Agent loop failed',
          retryCount: 0,
        };
        activeBuild.errors.push(activeBuild.lastError);
        notifyCallbacks('onStatusChange', 'failed', 'building');
        notifyProgressUpdate();
        
        // Persist failure state
        await persistBuildState('failed');
        
        // Move to completed and process next
        moveActiveToCompleted();
        processNextInQueue().catch(err => {
          console.error('[BuilderService] Error processing next in queue:', err);
        });
      }
    });
    
    return activeBuild;
  } catch (error) {
    console.error('[BuilderService] Build initialization failed:', error);
    
    if (activeBuild) {
      activeBuild.status = 'failed';
      activeBuild.lastError = {
        timestamp: new Date().toISOString(),
        message: error instanceof Error ? error.message : 'Unknown error',
        retryCount: 0,
      };
      activeBuild.errors.push(activeBuild.lastError);
    }
    
    throw error;
  }
}

/**
 * Pause the current build
 * Sets flag that agent loop checks after each area
 */
export async function pauseBuild(): Promise<BuildProgress | null> {
  if (!activeBuild || activeBuild.status !== 'building') {
    console.warn('[BuilderService] No active build to pause');
    return activeBuild;
  }
  
  console.log('[BuilderService] Pause requested');
  pauseRequested = true;
  
  // Also notify backend
  const authHeader = authService.getAuthHeader();
  if (authHeader) {
    try {
      await fetch(`${backendClient.baseUrl}/plans/${activeBuild.planId}/build/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
        },
      });
    } catch (error) {
      console.error('[BuilderService] Failed to notify backend of pause:', error);
    }
  }
  
  return activeBuild;
}

/**
 * Resume a paused build
 * @param planId - Plan ID to validate (must match current build)
 */
export async function resumeBuild(planId: string): Promise<BuildProgress | null> {
  if (!activeBuild || activeBuild.status !== 'paused') {
    console.warn('[BuilderService] No paused build to resume');
    return activeBuild;
  }
  
  if (planId !== activeBuild.planId) {
    console.error(`[BuilderService] Plan ID mismatch: expected ${activeBuild.planId}, got ${planId}`);
    throw new Error(`Plan ID mismatch: cannot resume build for different plan`);
  }
  
  console.log('[BuilderService] Resuming build');
  pauseRequested = false;
  
  const previousStatus = activeBuild.status;
  activeBuild.status = 'building';
  activeBuild.pausedAt = undefined;
  
  // Notify backend
  const authHeader = authService.getAuthHeader();
  if (authHeader) {
    try {
      await fetch(`${backendClient.baseUrl}/plans/${activeBuild.planId}/build/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
        },
      });
    } catch (error) {
      console.error('[BuilderService] Failed to notify backend of resume:', error);
    }
  }
  
  notifyCallbacks('onStatusChange', 'building', previousStatus);
  notifyProgressUpdate();
  
  // Persist active state
  await persistBuildState('active');
  
  // Agent loop will be resumed in Step 5
  
  return activeBuild;
}

/**
 * Cancel the current build
 * Stops agent and calls cleanup endpoint
 * @param planId - Plan ID to validate (must match current build)
 */
export async function cancelBuild(planId: string): Promise<BuildProgress | null> {
  if (!activeBuild) {
    console.warn('[BuilderService] No build to cancel');
    return null;
  }
  
  if (planId !== activeBuild.planId) {
    console.error(`[BuilderService] Plan ID mismatch: expected ${activeBuild.planId}, got ${planId}`);
    throw new Error(`Plan ID mismatch: cannot cancel build for different plan`);
  }
  
  if (activeBuild.status === 'completed' || activeBuild.status === 'cancelled') {
    console.warn('[BuilderService] Build already finished');
    return activeBuild;
  }
  
  console.log('[BuilderService] Cancelling build');
  cancelRequested = true;
  
  const previousStatus = activeBuild.status;
  activeBuild.status = 'cancelled';
  
  // Notify backend
  const authHeader = authService.getAuthHeader();
  if (authHeader) {
    try {
      await fetch(`${backendClient.baseUrl}/plans/${activeBuild.planId}/build/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
        },
      });
    } catch (error) {
      console.error('[BuilderService] Failed to notify backend of cancel:', error);
    }
  }
  
  // Clean up conversation
  const globalState = GlobalStateManager.getInstance();
  const conversationId = `builder_${activeBuild.planId}`;
  globalState.removeConversation(conversationId);
  
  notifyCallbacks('onStatusChange', 'cancelled', previousStatus);
  notifyProgressUpdate();
  
  return activeBuild;
}

// ═══════════════════════════════════════════════════════════════════════════
// AGENT EXECUTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Run the Multi-Slide Builder Agent
 * This is the main build loop that runs asynchronously
 * 
 * @param planId - Plan ID
 * @param plan - Presentation plan
 * @param styleGuidedocumentId - Style guide deck ID for search
 * @param workingPresentationPath - Path to working presentation
 * @param workingWorkbookPath - Path to working workbook
 * @param slideDimensions - Slide dimensions in points
 */
async function runBuildAgent(
  planId: string,
  plan: PresentationPlan,
  styleGuidedocumentId: string,
  workingPresentationPath: string,
  workingWorkbookPath: string,
  slideDimensions: { width: number; height: number }
): Promise<void> {
  console.log('[BuilderService] Starting Multi-Slide Builder Agent');
  
  // Get the conversation's AgentRegistry to properly register the agent
  const globalState = GlobalStateManager.getInstance();
  const conversationId = `builder_${planId}`;
  const conversation = globalState.getConversation(conversationId);
  
  if (!conversation) {
    throw new Error(`[BuilderService] Conversation not found: ${conversationId}`);
  }
  
  // Create the agent
  const agent = new MultiSlideBuilderAgent();
  
  // Register agent with the conversation's AgentRegistry (links parentRegistry + threadManager)
  conversation.agentRegistry.registerAgent('multiSlideBuilder', agent);
  
  // Build params for the agent
  // Note: PresentationPlan has sections[].name (not title), slides[].name (not title)
  const agentParams: MultiSlideBuilderParams & { conversationId: string; documentId: string } = {
    planId,
    plan: {
      planId,
      title: plan.sections[0]?.name || 'Untitled Presentation',
      sections: plan.sections.map(section => ({
        id: section.id,
        name: section.name,
        slides: section.slides.map(slide => ({
          slideId: slide.id,
          name: slide.name,
          slidePurpose: slide.purpose || '',
        })),
      })),
      slideCount: plan.sections.reduce((sum, s) => sum + s.slides.length, 0),
    },
    uiDescription: `Building ${plan.sections.reduce((sum, s) => sum + s.slides.length, 0)} slides across ${plan.sections.length} sections`,
    styleGuidedocumentId,
    workingPresentationPath,
    workingWorkbookPath,
    slideDimensions,
    styleGuideSlideCount: 0, // Will be set by initBuild response
    // Pass context for proper thread isolation
    conversationId,
    documentId: conversation.presentationId || styleGuidedocumentId,
  };
  
  // Add system message at start
  addBuildMessage(planId, {
    type: 'system',
    content: `Starting build for "${plan.sections[0]?.name || 'Untitled'}" with ${plan.sections.length} sections`,
  });
  
  // Execute the agent
  const result = await agent.execute(agentParams, {
    // ═══════════════════════════════════════════════════════════════════════
    // TOP-LEVEL CALLBACKS (MultiSlideBuilderAgent)
    // ═══════════════════════════════════════════════════════════════════════
    
    onChunk: (chunk) => {
      // Capture MultiSlideBuilderAgent's reasoning text (if any)
      if (chunk && chunk.trim()) {
        console.log(`[BuilderService] Agent chunk: ${chunk.substring(0, 100)}...`);
        addBuildMessage(planId, {
          type: 'assistant',
          content: chunk,
        });
      }
    },
    
    onToolCall: (toolName, params) => {
      console.log(`[BuilderService] Agent tool call: ${toolName}`);
      
      // Track tool call message
      addBuildMessage(planId, {
        type: 'tool_call',
        toolCall: {
          name: toolName,
          params: typeof params === 'string' ? JSON.parse(params) : params as Record<string, unknown>,
          status: 'running',
        },
      });
      
      // Check for pause/cancel
      if (shouldCancel()) {
        throw new Error('Build cancelled by user');
      }
      
      if (shouldPause()) {
        // Pause will be handled by the agent's natural stopping point
        console.log('[BuilderService] Pause requested - agent will stop after current operation');
      }
    },
    
    onToolResult: (toolName, result) => {
      console.log(`[BuilderService] Agent tool result: ${toolName}`, result);
      
      // Update the tool call message with result
      updateLastToolMessage(planId, 'complete', result);
      
      // Update progress based on tool results
      if (toolName === 'executeSection' && result && typeof result === 'object') {
        const sectionResult = result as Record<string, unknown>;
        const sectionIndex = activeBuild?.currentSectionIndex ?? 0;
        
        updateSectionStatus(
          sectionIndex,
          sectionResult.success ? 'completed' : 'failed',
          sectionResult.slidesBuilt as number
        );
        
        // Move to next section
        if (activeBuild && sectionIndex < activeBuild.totalSections - 1) {
          activeBuild.currentSectionIndex = sectionIndex + 1;
        }
        
        // Add completion message for section
        const sectionName = activeBuild?.sections[sectionIndex]?.sectionName || `Section ${sectionIndex + 1}`;
        addBuildMessage(planId, {
          type: 'system',
          content: sectionResult.success 
            ? `✓ Completed ${sectionName} (${sectionResult.slidesBuilt} slides)`
            : `✗ Failed to build ${sectionName}`,
        });
      }
    },
    
    onStatusChange: (status) => {
      console.log(`[BuilderService] Agent status: ${status}`);
      setCurrentPhase(status === 'executing' ? 'building' : undefined);
    },
    
    // ═══════════════════════════════════════════════════════════════════════
    // SUB-AGENT CALLBACKS (presentationLibrarySearchAgent, alphaSenseAgent, etc.)
    // ═══════════════════════════════════════════════════════════════════════
    
    onAgentChunk: (agentName, chunk) => {
      // Capture sub-agent's reasoning text (streaming)
      if (chunk && chunk.trim()) {
        console.log(`[BuilderService] Sub-agent ${agentName} chunk: ${chunk.substring(0, 50)}...`);
        addBuildMessage(planId, {
          type: 'assistant',
          content: chunk,
          agentName, // Track which agent this came from
        });
      }
    },
    
    onAgentToolCall: (agentName, toolName, args) => {
      console.log(`[BuilderService] Sub-agent ${agentName} tool call: ${toolName}`);
      
      // Track nested tool call
      addBuildMessage(planId, {
        type: 'tool_call',
        toolCall: {
          name: toolName,
          params: typeof args === 'string' ? JSON.parse(args) : args as Record<string, unknown>,
          status: 'running',
        },
        agentName, // Track which agent this tool belongs to
      });
    },
    
    onAgentToolResult: (agentName, toolName, result) => {
      console.log(`[BuilderService] Sub-agent ${agentName} tool result: ${toolName}`);
      
      // Update the nested tool call with result
      updateLastToolMessage(planId, 'complete', result);
    },
  });
  
  console.log('[BuilderService] Multi-Slide Builder Agent completed', result);
  
  // Update final state
  // AgentResult has 'status' ('success'|'partial'|'error'), not 'success' boolean
  if (activeBuild) {
    const isSuccess = result.status === 'success' || result.status === 'partial';
    if (isSuccess) {
      const previousStatus = activeBuild.status;
      activeBuild.status = 'completed';
      activeBuild.completedAt = new Date().toISOString();
      activeBuild.percentComplete = 100;
      
      // Add completion message
      addBuildMessage(planId, {
        type: 'system',
        content: `✓ Build completed successfully! ${activeBuild.slidesBuilt} slides built.`,
      });
      
      notifyCallbacks('onStatusChange', 'completed', previousStatus);
      
      // Persist completed state
      await persistBuildState('completed');
    } else {
      const previousStatus = activeBuild.status;
      activeBuild.status = 'failed';
      // Extract error message from result - rationale contains summary
      const errorMessage = result.error || result.results[0]?.rationale || 'Build failed';
      activeBuild.lastError = {
        timestamp: new Date().toISOString(),
        message: errorMessage,
        retryCount: 0,
      };
      activeBuild.errors.push(activeBuild.lastError);
      
      // Add error message
      addBuildMessage(planId, {
        type: 'error',
        content: `✗ Build failed: ${errorMessage}`,
      });
      
      notifyCallbacks('onStatusChange', 'failed', previousStatus);
      
      // Persist failed state
      await persistBuildState('failed');
    }
    notifyProgressUpdate();
    
    // Move to completed list and process next in queue
    moveActiveToCompleted();
    processNextInQueue().catch(err => {
      console.error('[BuilderService] Error processing next in queue:', err);
    });
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// STATE ACCESS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get current build state
 * Called by dialog UI to display progress
 */
export function getBuildState(): BuildProgress | null {
  return activeBuild;
}

/**
 * Check if a build is currently active
 */
export function isBuilding(): boolean {
  return activeBuild?.status === 'building';
}

/**
 * Check if pause has been requested
 * Called by agent loop to know when to stop
 */
export function shouldPause(): boolean {
  return pauseRequested;
}

/**
 * Check if cancel has been requested
 * Called by agent loop to know when to abort
 */
export function shouldCancel(): boolean {
  return cancelRequested;
}

/**
 * Check if service is initialized
 */
export function isBuilderServiceInitialized(): boolean {
  return isServiceInitialized;
}

// ═══════════════════════════════════════════════════════════════════════════
// QUEUE MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the current queue state
 * Returns both active build and queued builds
 */
export function getQueueState(): { active: BuildProgress | null; queue: QueuedBuildEntry[]; completed: BuildProgress[] } {
  return {
    active: activeBuild,
    queue: [...buildQueue],
    completed: [...completedBuilds],
  };
}

/**
 * Add a build to the queue
 */
export async function enqueueBuild(entry: QueuedBuildEntry): Promise<void> {
  // Update position to end of queue
  entry.position = buildQueue.length;
  buildQueue.push(entry);
  console.log(`[BuilderService] Enqueued build ${entry.planId} at position ${entry.position}`);
  notifyProgressUpdate();
  
  // Sync to backend
  await syncQueueToBackend();
}

/**
 * Remove a build from the queue by planId
 * @returns true if found and removed
 */
export async function dequeueByPlanId(planId: string): Promise<boolean> {
  const index = buildQueue.findIndex(e => e.planId === planId);
  if (index === -1) {
    return false;
  }
  
  buildQueue.splice(index, 1);
  
  // Reorder positions
  reorderQueuePositions();
  
  console.log(`[BuilderService] Dequeued build ${planId}`);
  notifyProgressUpdate();
  
  // Sync to backend
  await syncQueueToBackend();
  
  return true;
}

/**
 * Get queue position for a plan (1-based for UI)
 * @returns Position number or null if not in queue
 */
export function getQueuePosition(planId: string): number | null {
  const index = buildQueue.findIndex(e => e.planId === planId);
  return index >= 0 ? index + 1 : null;
}

/**
 * Reorder queue positions after modification
 * Called after splice/shift operations to keep positions consistent
 */
function reorderQueuePositions(): void {
  buildQueue.forEach((entry, i) => {
    entry.position = i;
  });
}

/**
 * Create a BuildProgress object from a queued entry (for UI display)
 */
function createQueuedBuildProgress(entry: QueuedBuildEntry): BuildProgress {
  return {
    planId: entry.planId,
    planTitle: entry.planTitle,
    status: 'queued',
    currentSectionIndex: 0,
    totalSections: entry.totalSections,
    sections: [],
    slidesBuilt: 0,
    totalSlides: entry.totalSlides,
    percentComplete: 0,
    errors: [],
  };
}

/**
 * Process the next build in queue (called when active build completes)
 */
async function processNextInQueue(): Promise<void> {
  if (buildQueue.length === 0) {
    console.log('[BuilderService] Queue is empty, no next build to process');
    return;
  }
  
  if (activeBuild && activeBuild.status === 'building') {
    console.log('[BuilderService] Cannot process next - a build is still active');
    return;
  }
  
  const nextEntry = buildQueue.shift();
  if (!nextEntry) return;
  
  // Reorder remaining queue positions
  reorderQueuePositions();
  
  console.log(`[BuilderService] Processing next queued build: ${nextEntry.planId}`);
  
  // Start the build (this will set activeBuild)
  try {
    await startBuildFromQueueEntry(nextEntry);
  } catch (error) {
    console.error(`[BuilderService] Failed to start queued build ${nextEntry.planId}:`, error);
    // Try the next one
    await processNextInQueue();
  }
}

/**
 * Start a build from a queue entry (internal)
 */
async function startBuildFromQueueEntry(entry: QueuedBuildEntry): Promise<BuildProgress> {
  // Directly start without re-queueing (uses internal function)
  return startBuildInternal(
    entry.planId,
    entry.plan,
    entry.styleGuidedocumentId,
    entry.styleGuidePath,
    entry.slidesBase64
  );
}

/**
 * Promote a queued build to active (pauses current if running)
 * @param planId - The plan ID to promote
 * @returns The promoted build's progress
 */
export async function promoteQueuedBuild(planId: string): Promise<BuildProgress | null> {
  const queueIndex = buildQueue.findIndex(e => e.planId === planId);
  if (queueIndex === -1) {
    console.warn(`[BuilderService] Cannot promote - plan ${planId} not in queue`);
    return null;
  }
  
  // Pause current build if running
  if (activeBuild && activeBuild.status === 'building') {
    console.log(`[BuilderService] Pausing current build ${activeBuild.planId} to promote ${planId}`);
    await pauseBuild();
  }
  
  // Remove from queue
  const [entry] = buildQueue.splice(queueIndex, 1);
  
  // Reorder remaining queue positions
  reorderQueuePositions();
  
  // Sync queue change to backend
  await syncQueueToBackend();
  
  // Start the promoted build
  console.log(`[BuilderService] Promoting queued build: ${planId}`);
  return startBuildFromQueueEntry(entry);
}

/**
 * Clear completed builds older than specified minutes
 */
function clearOldCompletedBuilds(maxAgeMinutes: number = 5): void {
  const cutoff = Date.now() - (maxAgeMinutes * 60 * 1000);
  completedBuilds = completedBuilds.filter(build => {
    const completedTime = build.completedAt ? new Date(build.completedAt).getTime() : 0;
    return completedTime > cutoff;
  });
}

/**
 * Move active build to completed list
 */
function moveActiveToCompleted(): void {
  if (activeBuild && (activeBuild.status === 'completed' || activeBuild.status === 'failed' || activeBuild.status === 'cancelled')) {
    // Mark as unseen when moving to completed (user hasn't viewed it yet)
    activeBuild.seen = false;
    completedBuilds.unshift(activeBuild);
    clearOldCompletedBuilds();
    activeBuild = null;
    activeBuildParams = null;
  }
}

/**
 * Mark a completed build as "seen" (removes the "New" badge in UI)
 * @param planId - The plan ID of the build to mark as seen
 * @returns true if the build was found and marked, false otherwise
 */
export function markBuildAsSeen(planId: string): boolean {
  const build = completedBuilds.find(b => b.planId === planId);
  if (build) {
    build.seen = true;
    notifyProgressUpdate();
    return true;
  }
  return false;
}

/**
 * Get count of unseen completed builds
 */
export function getUnseenCompletedCount(): number {
  return completedBuilds.filter(b => !b.seen).length;
}

/**
 * Start a build immediately, pausing any active build and moving it to queue top
 * 
 * @param planId - The plan ID to build
 * @param plan - The plan object with sections and slides
 * @param styleGuidedocumentId - ID of the style guide deck
 * @param styleGuidePath - Optional path to style guide file
 * @param slidesBase64 - Optional slides as base64 (for live style guides)
 * @returns The build progress for the new build
 */
export async function startBuildImmediately(
  planId: string,
  plan: PresentationPlan,
  styleGuidedocumentId: string,
  styleGuidePath?: string,
  slidesBase64?: string[]
): Promise<BuildProgress> {
  console.log(`[BuilderService] startBuildImmediately called for plan: ${planId}`);
  
  // If there's an active build, pause it and move to top of queue
  if (activeBuild && activeBuildParams && (activeBuild.status === 'building' || activeBuild.status === 'initializing')) {
    console.log(`[BuilderService] Pausing active build: ${activeBuild.planId}`);
    
    // Set pause flag so the agent knows to stop at next checkpoint
    // Note: This is cooperative pausing - the agent will check shouldPause() 
    // and stop gracefully at the next safe point
    pauseRequested = true;
    
    // Create a queue entry from the active build for later resume
    const activeEntry: QueuedBuildEntry = {
      planId: activeBuild.planId,
      planTitle: activeBuild.planTitle,
      position: 0, // Will be at top
      status: 'queued',
      enqueuedAt: new Date().toISOString(),
      conversationId: activeBuildParams.conversationId,
      documentId: activeBuildParams.documentId,
      styleGuidedocumentId: activeBuildParams.styleGuidedocumentId,
      styleGuidePath: activeBuildParams.styleGuidePath,
      slidesBase64: activeBuildParams.slidesBase64,
      plan: activeBuildParams.plan,
      totalSlides: activeBuild.totalSlides,
      totalSections: activeBuild.totalSections,
    };
    
    // Store reference to paused build before clearing
    const pausedBuild = activeBuild;
    
    // Add paused build to top of queue
    buildQueue.unshift(activeEntry);
    reorderQueuePositions();
    
    // Mark as paused with timestamp
    pausedBuild.status = 'paused';
    pausedBuild.pausedAt = new Date().toISOString();
    
    // Clear active build and params BEFORE resetting pause flag
    // This ensures the agent sees pauseRequested=true if it's still running
    activeBuild = null;
    activeBuildParams = null;
    
    // Sync queue to backend
    await syncQueueToBackend();
    
    // Reset pause flag AFTER queue is synced
    // The agent will have either already stopped or will stop at next checkpoint
    pauseRequested = false;
    
    console.log(`[BuilderService] Paused build moved to queue top`);
  }
  
  // Now start the new build (it will become the active build)
  return startBuild(planId, plan, styleGuidedocumentId, styleGuidePath, slidesBase64);
}

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS UPDATES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Update progress from agent (called by MultiSlideBuilderAgent)
 * 
 * @param updates - Partial progress updates
 */
export function updateProgress(updates: Partial<BuildProgress>): void {
  if (!activeBuild) return;
  
  Object.assign(activeBuild, updates);
  
  // Recalculate percent
  if (updates.slidesBuilt !== undefined) {
    activeBuild.percentComplete = (activeBuild.slidesBuilt / activeBuild.totalSlides) * 100;
  }
  
  notifyProgressUpdate();
}

/**
 * Update section status (called by agent)
 */
export function updateSectionStatus(
  sectionIndex: number, 
  status: SectionProgress['status'],
  slidesBuilt?: number
): void {
  if (!activeBuild || !activeBuild.sections[sectionIndex]) return;
  
  activeBuild.sections[sectionIndex].status = status;
  
  if (slidesBuilt !== undefined) {
    activeBuild.sections[sectionIndex].slidesBuilt = slidesBuilt;
  }
  
  if (status === 'building' && !activeBuild.sections[sectionIndex].startedAt) {
    activeBuild.sections[sectionIndex].startedAt = new Date().toISOString();
  }
  
  if (status === 'completed' || status === 'failed' || status === 'skipped') {
    activeBuild.sections[sectionIndex].completedAt = new Date().toISOString();
  }
  
  notifyProgressUpdate();
}

/**
 * Set current area being built (for UI display)
 */
export function setCurrentArea(areaId: string | undefined): void {
  if (!activeBuild) return;
  
  activeBuild.currentArea = areaId;
  
  if (activeBuild.sections[activeBuild.currentSectionIndex]) {
    activeBuild.sections[activeBuild.currentSectionIndex].currentAreaId = areaId;
  }
  
  notifyProgressUpdate();
}

/**
 * Set current build phase
 */
export function setCurrentPhase(phase: BuildProgress['currentPhase']): void {
  if (!activeBuild) return;
  
  activeBuild.currentPhase = phase;
  notifyProgressUpdate();
}

/**
 * Add an error to the build
 */
export function addBuildError(error: Omit<BuildError, 'timestamp'>): void {
  if (!activeBuild) return;
  
  const fullError: BuildError = {
    ...error,
    timestamp: new Date().toISOString(),
  };
  
  activeBuild.errors.push(fullError);
  activeBuild.lastError = fullError;
  
  notifyCallbacks('onError', fullError);
  notifyProgressUpdate();
}

// ═══════════════════════════════════════════════════════════════════════════
// CALLBACK MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Register callbacks for progress updates
 * @returns Unsubscribe function
 */
export function registerProgressCallbacks(callbacks: BuildProgressCallbacks): () => void {
  progressCallbacks.add(callbacks);
  console.log(`[BuilderService] Registered progress callbacks (total: ${progressCallbacks.size})`);
  
  return () => {
    progressCallbacks.delete(callbacks);
    console.log(`[BuilderService] Unregistered progress callbacks (total: ${progressCallbacks.size})`);
  };
}

/**
 * Notify all registered callbacks about a progress update
 */
function notifyProgressUpdate(): void {
  if (!activeBuild) return;
  
  progressCallbacks.forEach(callbacks => {
    try {
      callbacks.onProgressUpdate?.(activeBuild!);
    } catch (error) {
      console.error('[BuilderService] Error in progress callback:', error);
    }
  });
}

/**
 * Notify specific callback type
 */
function notifyCallbacks(type: keyof BuildProgressCallbacks, ...args: any[]): void {
  progressCallbacks.forEach(callbacks => {
    try {
      const fn = callbacks[type] as ((...args: any[]) => void) | undefined;
      fn?.(...args);
    } catch (error) {
      console.error(`[BuilderService] Error in ${type} callback:`, error);
    }
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// CLEANUP
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Reset the builder service
 * Called when add-in is closing or user logs out
 */
export function resetBuilderService(): void {
  console.log('[BuilderService] Resetting service');
  
  // Clean up WebSocket handler
  if (wsProgressHandler) {
    backendClient.offProgress(wsProgressHandler);
    wsProgressHandler = null;
  }
  
  // Clear state
  activeBuild = null;
  activeBuildParams = null;
  buildQueue = [];
  completedBuilds = [];
  pauseRequested = false;
  cancelRequested = false;
  progressCallbacks.clear();
  
  isServiceInitialized = false;
}

/**
 * Clear current build (after completion or when starting fresh)
 */
export function clearBuild(): void {
  if (activeBuild) {
    // Clean up conversation if exists
    const globalState = GlobalStateManager.getInstance();
    const conversationId = `builder_${activeBuild.planId}`;
    globalState.removeConversation(conversationId);
  }
  
  activeBuild = null;
  activeBuildParams = null;
  pauseRequested = false;
  cancelRequested = false;
}

/**
 * Clear the entire queue
 */
export async function clearQueue(): Promise<void> {
  // Clean up conversations for queued builds
  const globalState = GlobalStateManager.getInstance();
  for (const entry of buildQueue) {
    globalState.removeConversation(entry.conversationId);
  }
  
  buildQueue = [];
  console.log('[BuilderService] Queue cleared');
  notifyProgressUpdate();
  
  // Sync queue change to backend
  await syncQueueToBackend();
}

// ═══════════════════════════════════════════════════════════════════════════
// PERSISTENCE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Map BuildStatus to PersistedConversationStatus
 */
function mapBuildStatusToPersisted(status: BuildStatus): PersistedConversationStatus {
  switch (status) {
    case 'building':
    case 'initializing':
      return 'active';
    case 'paused':
      return 'paused';
    case 'completed':
      return 'completed';
    case 'failed':
    case 'cancelled':
      return 'failed';
    case 'queued':
    case 'idle':
    default:
      return 'active';
  }
}

/**
 * Map SectionProgress status to PersistedSectionState status
 */
function mapSectionStatus(status: SectionProgress['status']): PersistedSectionState['status'] {
  switch (status) {
    case 'building':
      return 'in_progress';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'skipped':
    case 'pending':
    default:
      return 'pending';
  }
}

/**
 * Persist the current build state to disk
 * 
 * @param status - The status to save (may differ from activeBuild.status for transition states)
 */
async function persistBuildState(status: PersistedConversationStatus): Promise<void> {
  if (!activeBuild) {
    console.log('[BuilderService] No active build to persist');
    return;
  }
  
  try {
    const globalState = GlobalStateManager.getInstance();
    const conversationId = `builder_${activeBuild.planId}`;
    
    // Check if conversation exists in registry
    const conversation = globalState.getConversation(conversationId);
    if (!conversation) {
      console.warn(`[BuilderService] Conversation not found for persistence: ${conversationId}`);
      return;
    }
    
    // Build PersistedBuildProgress from current state
    const buildProgress: PersistedBuildProgress = {
      planId: activeBuild.planId,
      status: status === 'active' ? 'active' : 
              status === 'paused' ? 'paused' :
              status === 'completed' ? 'completed' : 'failed',
      currentSectionIndex: activeBuild.currentSectionIndex,
      totalSections: activeBuild.totalSections,
      slidesBuilt: activeBuild.slidesBuilt,
      totalSlides: activeBuild.totalSlides,
      sectionStates: activeBuild.sections.map(s => ({
        sectionIndex: s.sectionIndex,
        sectionTitle: s.sectionName,
        status: mapSectionStatus(s.status),
        slidesCompleted: s.slidesBuilt,
        totalSlides: s.totalSlides,
        error: undefined,
      })),
      startedAt: activeBuild.startedAt,
      pausedAt: activeBuild.pausedAt,
      completedAt: activeBuild.completedAt,
    };
    
    // Persist the conversation with build progress
    await globalState.persistConversation(conversationId, {
      title: activeBuild.planTitle,
      planId: activeBuild.planId,
      status,
      buildProgress,
    });
    
    console.log(`[BuilderService] Persisted build state: ${activeBuild.planId} (${status})`);
  } catch (error) {
    console.error('[BuilderService] Failed to persist build state:', error);
    // Don't throw - persistence failure shouldn't stop the build
  }
}

/**
 * Sync queue state to the Python backend
 * Called whenever queue changes (add, remove, reorder)
 */
async function syncQueueToBackend(): Promise<void> {
  try {
    const authHeader = authService.getAuthHeader();
    
    const state = {
      version: '1.0.0',
      queue: buildQueue.map((entry, i) => ({
        planId: entry.planId,
        planTitle: entry.planTitle,
        position: i,
        status: entry.status,
        enqueuedAt: entry.enqueuedAt,
        conversationId: entry.conversationId,
        documentId: entry.documentId,
        styleGuidedocumentId: entry.styleGuidedocumentId,
        // Don't include slidesBase64 - too large and not needed for persistence
        progress: null,
      })),
      activePlanId: activeBuild?.planId || null,
      lastUpdated: new Date().toISOString(),
    };
    
    const response = await fetch(`${backendClient.baseUrl}/build/queue`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { 'Authorization': authHeader } : {}),
      },
      body: JSON.stringify(state),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[BuilderService] Failed to sync queue to backend:', errorText);
    } else {
      console.log(`[BuilderService] Synced queue to backend: ${buildQueue.length} queued builds`);
    }
  } catch (error) {
    console.error('[BuilderService] Error syncing queue to backend:', error);
    // Don't throw - sync failure shouldn't break the queue
  }
}

/**
 * Load queue state from the Python backend
 * Called during initialization
 */
async function loadQueueFromBackend(): Promise<void> {
  try {
    const authHeader = authService.getAuthHeader();
    
    const response = await fetch(`${backendClient.baseUrl}/build/queue`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { 'Authorization': authHeader } : {}),
      },
    });
    
    if (!response.ok) {
      console.warn('[BuilderService] Could not load queue from backend');
      return;
    }
    
    const state = await response.json();
    console.log(`[BuilderService] Loaded queue state from backend: ${state.queue?.length || 0} items`);
    
    // Note: We don't restore queue entries here because they need their full plan data
    // which isn't stored in the queue state. The loadPersistedBuilds function handles
    // restoring full conversation data including plans.
    
  } catch (error) {
    console.error('[BuilderService] Error loading queue from backend:', error);
  }
}

/**
 * Load persisted builds from disk on startup
 * Restores paused/active builds to the queue so they can be resumed
 */
async function loadPersistedBuilds(): Promise<void> {
  console.log('[BuilderService] Loading persisted builds...');
  
  try {
    const globalState = GlobalStateManager.getInstance();
    const persistence = globalState.getConversationPersistence();
    
    // Load builder conversations that are paused or were active
    const builderConvos = await persistence.listConversations({ origin: 'builder' });
    const resumable = builderConvos.filter(c => 
      c.status === 'paused' || c.status === 'active'
    );
    
    console.log(`[BuilderService] Found ${resumable.length} resumable builds`);
    
    for (const meta of resumable) {
      try {
        // Load full conversation
        const loaded = await persistence.loadConversationForRestore(meta.conversationId);
        if (!loaded || !loaded.buildProgress) {
          console.warn(`[BuilderService] No build progress found for ${meta.conversationId}`);
          continue;
        }
        
        // Restore conversation to GlobalStateManager (restores threads and context)
        await globalState.loadPersistedConversation(meta.conversationId);
        
        // Create a BuildProgress from the persisted state
        const buildProgress: BuildProgress = {
          planId: loaded.buildProgress.planId,
          planTitle: meta.title,
          status: 'paused', // Always restore as paused - user must manually resume
          currentSectionIndex: loaded.buildProgress.currentSectionIndex,
          totalSections: loaded.buildProgress.totalSections,
          slidesBuilt: loaded.buildProgress.slidesBuilt,
          totalSlides: loaded.buildProgress.totalSlides,
          percentComplete: loaded.buildProgress.totalSlides > 0 
            ? (loaded.buildProgress.slidesBuilt / loaded.buildProgress.totalSlides) * 100 
            : 0,
          sections: loaded.buildProgress.sectionStates?.map(s => ({
            sectionId: `section_${s.sectionIndex}`,
            sectionIndex: s.sectionIndex,
            sectionName: s.sectionTitle,
            status: s.status === 'in_progress' ? 'building' :
                    s.status === 'completed' ? 'completed' :
                    s.status === 'failed' ? 'failed' : 'pending',
            slidesBuilt: s.slidesCompleted,
            totalSlides: s.totalSlides,
          })) || [],
          errors: [],
          startedAt: loaded.buildProgress.startedAt,
          pausedAt: loaded.buildProgress.pausedAt,
          completedAt: loaded.buildProgress.completedAt,
        };
        
        // Add to completed builds list for UI display
        // Users can click to view activity log and potentially resume
        completedBuilds.push(buildProgress);
        
        console.log(`[BuilderService] Restored persisted build: ${meta.conversationId} (${buildProgress.slidesBuilt}/${buildProgress.totalSlides} slides)`);
      } catch (error) {
        console.error(`[BuilderService] Failed to restore build ${meta.conversationId}:`, error);
      }
    }
    
    // Also load queue state from backend (for reference)
    await loadQueueFromBackend();
    
    notifyProgressUpdate();
    console.log(`[BuilderService] Loaded ${completedBuilds.length} persisted builds`);
    
  } catch (error) {
    console.error('[BuilderService] Error loading persisted builds:', error);
  }
}

